import { createTheme } from "@mui/material/styles";

// The people accent: contact cards and the contact drawer they open share it.
interface PeopleColor {
  /** Ring and hover border. */
  main: string;
  /** The card's and the drawer header's background. */
  light: string;
  /** Text on `light`. */
  dark: string;
  /** The card's resting hairline. */
  border: string;
}

declare module "@mui/material/styles" {
  interface Palette {
    people: PeopleColor;
  }
  interface PaletteOptions {
    people?: PeopleColor;
  }
}

const theme = createTheme({
  cssVariables: {
    colorSchemeSelector: 'data-mui-color-scheme',
  },
  colorSchemes: {
    light: {
      palette: {
        primary: {
          main: "#602df2",
          light: "#6d3ef3",
          dark: "#4a1fd4",
          contrastText: "#ffffff",
        },
        secondary: {
          main: "#70e0b1",
          dark: "#00302a",
        },
        error: {
          main: "#fb4e63",
          light: "#ffeaec",
        },
        warning: {
          main: "#ff974b",
          light: "#fff3e9",
        },
        success: {
          main: "#00bb9e",
          light: "#e0f7f3",
        },
        background: {
          default: "#ffffff",
          paper: "#f5f5f6",
        },
        text: {
          primary: "#0d0b13",
          secondary: "#6e6d71",
        },
        divider: "#d8d8d9",
        people: {
          main: "#2fbf86",
          light: "#eaf8f1",
          dark: "#0b4a3a",
          border: "#cdeedf",
        },
      },
    },
    dark: {
      palette: {
        primary: {
          main: "#8b6cf5",
          light: "#a78bfa",
          dark: "#602df2",
          contrastText: "#ffffff",
        },
        secondary: {
          main: "#70e0b1",
          dark: "#00302a",
        },
        error: {
          main: "#fb4e63",
          light: "#3d1a20",
        },
        warning: {
          main: "#ff974b",
          light: "#3d2a15",
        },
        success: {
          main: "#00bb9e",
          light: "#0d2e28",
        },
        background: {
          default: "#121218",
          paper: "#1a1a24",
        },
        text: {
          primary: "#e8e6f0",
          secondary: "#9e9da3",
        },
        divider: "#2e2e3a",
        people: {
          main: "#4fd6a0",
          light: "#14302a",
          dark: "#a6f0d1",
          border: "#1f4a3e",
        },
      },
    },
  },
  typography: {
    fontFamily: '"Inter", sans-serif',
    fontSize: 13,
    h1: { fontSize: "1.75rem", fontWeight: 700, lineHeight: 1.2 },
    h2: { fontSize: "1.5rem", fontWeight: 700, lineHeight: 1.25 },
    h3: { fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.3 },
    h4: { fontSize: "1.1rem", fontWeight: 600, lineHeight: 1.35 },
    h5: { fontSize: "0.95rem", fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: "0.875rem", fontWeight: 600, lineHeight: 1.4 },
    subtitle1: { fontSize: "0.8125rem", fontWeight: 500, lineHeight: 1.4 },
    subtitle2: { fontSize: "0.75rem", fontWeight: 500, lineHeight: 1.4 },
    body1: { fontSize: "0.8125rem", lineHeight: 1.5 },
    body2: { fontSize: "0.75rem", lineHeight: 1.5 },
    caption: { fontSize: "0.6875rem", lineHeight: 1.4 },
    overline: { fontSize: "0.625rem", lineHeight: 1.5, letterSpacing: "0.08em" },
    button: {
      fontSize: "0.8125rem",
      textTransform: "none",
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        :root {
          --mui-fontFamily-body: "Inter", sans-serif;
          --mui-fontFamily-code: "Inter", sans-serif;
        }
        html, body, *, *::before, *::after {
          font-family: "Inter", sans-serif;
        }
      `,
    },
    MuiButton: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          borderWidth: "1.5px",
          "&:hover": {
            borderWidth: "1.5px",
            ...(ownerState.variant === "contained" && ownerState.color === "primary" && {
              backgroundColor: theme.vars.palette.primary.light,
            }),
          },
        }),
        sizeSmall: {
          padding: "4px 12px",
          fontSize: "0.8125rem",
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        size: "small",
      },
    },
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
        },
        sizeSmall: {
          fontSize: "0.8125rem",
        },
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
          "& .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1,
          },
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        select: {
          fontSize: "0.8125rem",
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        size: "small",
      },
      styleOverrides: {
        listbox: {
          fontSize: "0.8125rem",
          "& .MuiAutocomplete-option": {
            padding: "4px 10px",
            minHeight: "unset",
          },
        },
        tag: {
          margin: "1px 2px",
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: "small",
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "0.6875rem",
          marginTop: 2,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
        },
      },
    },
    MuiChip: {
      defaultProps: {
        size: "small",
        variant: "filled",
      },
      styleOverrides: {
        root: {
          fontSize: "0.75rem",
          height: 24,
          borderRadius: 4,
        },
        sizeSmall: {
          height: 22,
        },
      },
    },
    // Content surfaces are grey outlined boxes with an 8px radius. Paper keeps
    // its elevated default: menus, popovers and dialogs render one and need the
    // shadow, so content Papers ask for `variant="outlined"` themselves.
    MuiPaper: {
      styleOverrides: {
        outlined: {
          borderRadius: 8,
        },
      },
    },
    MuiCard: {
      defaultProps: {
        variant: "outlined",
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiAccordion: {
      defaultProps: {
        disableGutters: true,
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid ${theme.vars.palette.divider}`,
          borderRadius: 8,
          "&:before": { display: "none" },
          "&:first-of-type, &:last-of-type": { borderRadius: 8 },
        }),
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: "12px 16px",
        },
        title: {
          fontSize: "0.875rem",
          fontWeight: 600,
        },
        subheader: {
          fontSize: "0.75rem",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "12px 16px",
          "&:last-child": {
            paddingBottom: 12,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
          padding: "4px 12px",
          alignItems: "center",
        },
        icon: {
          fontSize: "1.125rem",
          padding: "4px 0",
          marginRight: 8,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
          padding: "6px 12px",
        },
        head: ({ theme }) => ({
          fontSize: "0.75rem",
          fontWeight: 600,
          color: theme.vars.palette.text.secondary,
        }),
      },
    },
    // The last row's bottom border would double the container's outline.
    MuiTableBody: {
      styleOverrides: {
        root: {
          "& > .MuiTableRow-root:last-child > .MuiTableCell-root": {
            borderBottom: 0,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          fontSize: "0.75rem",
        },
        selectLabel: {
          fontSize: "0.75rem",
        },
        displayedRows: {
          fontSize: "0.75rem",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
          minHeight: 40,
          padding: "6px 16px",
          textTransform: "none",
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.8125rem",
          minHeight: "unset",
          padding: "6px 12px",
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: "0.8125rem",
        },
        secondary: {
          fontSize: "0.75rem",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          paddingTop: 4,
          paddingBottom: 4,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "0.6875rem",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          fontWeight: 600,
          padding: "12px 20px",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "12px 20px",
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "8px 20px 12px",
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize: "0.625rem",
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        color: "default",
      },
    },
  },
});

export default theme;
